import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const GARNET = '#5b1e22';
const COAL = '#181617';
const ASHEN = '#7A766F';
const GREEN = '#1a7a4c';
const ORANGE = '#b35c00';
const RED = '#b32d2d';

const RAG = { green: GREEN, amber: ORANGE, red: RED, unknown: ASHEN };
const TIER = { A: GARNET, B: '#2a5bd7', C: ASHEN };

const btn = (bg, disabled) => ({
  background: disabled ? '#ccc' : bg, color: '#fff', border: 'none', borderRadius: 8,
  padding: '9px 16px', fontSize: 14, fontWeight: 600, cursor: disabled ? 'default' : 'pointer',
});

function Stat({ label, value, color }) {
  return (
    <div style={{ background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
      <div style={{ fontSize: 11, color: ASHEN, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || COAL }}>{value}</div>
    </div>
  );
}

export default function StockMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [running, setRunning] = useState('');
  const [country, setCountry] = useState('all');
  const [onlyOrder, setOnlyOrder] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await api.get('/ops/stock-monitor/preview');
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load preview. Shopify may not be configured, or the store has no flatback/fashion-tagged products yet.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  const run = async (mode) => {
    const label = mode === 'po' ? 'generate monthly PO drafts' : mode === 'alerts' ? 'post low-stock alerts' : 'run a full pass (alerts + PO drafts)';
    if (!window.confirm(`This will ${label} into the approvals dashboard. Continue?`)) return;
    setRunning(mode);
    try {
      await api.post('/ops/stock-monitor/run', { mode });
      window.alert('Started in the background. PO drafts appear under Approvals; alerts under Alerts. Give it a minute.');
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to start run');
    } finally {
      setRunning('');
    }
  };

  const skus = (data?.skus || []).filter(s =>
    (country === 'all' || s.country === country) && (!onlyOrder || s.suggested_units > 0));
  const s = data?.summary || {};

  return (
    <div style={{ padding: 24, maxWidth: 1500, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 10 }}>
        <h1 style={{ margin: 0, fontSize: 24, color: GARNET }}>Stock Monitor</h1>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={loadPreview} disabled={loading} style={btn(COAL, loading)}>{loading ? 'Loading…' : '↻ Refresh preview'}</button>
          <button onClick={() => run('po')} disabled={!!running} style={btn(GARNET, !!running)}>{running === 'po' ? 'Starting…' : 'Generate PO drafts'}</button>
          <button onClick={() => run('alerts')} disabled={!!running} style={btn(ORANGE, !!running)}>{running === 'alerts' ? 'Starting…' : 'Post alerts'}</button>
        </div>
      </div>
      <p style={{ color: ASHEN, fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        Live velocity, tiering and reorder suggestions with the working-capital float overlay. Preview reads live Shopify inventory + your sales pace; it writes nothing. Use the action buttons to push drafts/alerts for review.
      </p>

      {error && <div style={{ background: '#fce8e8', color: RED, padding: 12, borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 18 }}>
          <Stat label="SKUs analysed" value={s.total ?? 0} />
          <Stat label="Critical" value={s.critical ?? 0} color={RED} />
          <Stat label="Warning" value={s.warning ?? 0} color={ORANGE} />
          <Stat label="To order" value={s.to_order ?? 0} color={GARNET} />
        </div>
      )}

      {data && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {['all', 'HK', 'SG'].map(c => (
              <button key={c} onClick={() => setCountry(c)} style={{
                padding: '6px 14px', borderRadius: 20, border: '1px solid #ddd', cursor: 'pointer',
                background: country === c ? GARNET : '#fff', color: country === c ? '#fff' : COAL, fontSize: 13, fontWeight: 600,
              }}>{c === 'all' ? 'All' : c}</button>
            ))}
          </div>
          <label style={{ fontSize: 13, color: COAL, display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="checkbox" checked={onlyOrder} onChange={e => setOnlyOrder(e.target.checked)} /> Only items needing an order
          </label>
          <span style={{ color: ASHEN, fontSize: 12, marginLeft: 'auto' }}>{skus.length} shown</span>
        </div>
      )}

      {data && (
        <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#faf8f8', textAlign: 'left', color: ASHEN, fontSize: 11 }}>
                <th style={{ padding: '10px 12px' }}>SKU</th>
                <th style={{ padding: '10px 12px' }}>Ctry</th>
                <th style={{ padding: '10px 12px' }}>Factory</th>
                <th style={{ padding: '10px 12px' }}>Tier</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Stock</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>On order</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>/day</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Cover</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Suggest</th>
                <th style={{ padding: '10px 12px', textAlign: 'right' }}>Float</th>
              </tr>
            </thead>
            <tbody>
              {skus.length === 0 && (
                <tr><td colSpan={10} style={{ padding: 24, textAlign: 'center', color: ASHEN }}>No SKUs to show. Load POs + run a Shopify sync so velocity and cost populate.</td></tr>
              )}
              {skus.map((v, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f0eded' }}>
                  <td style={{ padding: '9px 12px' }}><strong>{v.sku}</strong><div style={{ color: ASHEN }}>{v.product_title}</div></td>
                  <td style={{ padding: '9px 12px' }}>{v.country}</td>
                  <td style={{ padding: '9px 12px' }}>{v.factory_key}</td>
                  <td style={{ padding: '9px 12px' }}><span style={{ color: TIER[v.tier] || ASHEN, fontWeight: 700 }}>{v.tier}</span></td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: v.days_remaining <= 15 ? RED : v.days_remaining <= 45 ? ORANGE : COAL }}>{v.total_stock}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', color: ASHEN }}>{v.on_order}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}>{v.daily_velocity}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}>{v.days_remaining >= 999 ? '—' : `${v.days_remaining}d`}</td>
                  <td style={{ padding: '9px 12px', textAlign: 'right', fontWeight: v.suggested_units > 0 ? 700 : 400, color: v.suggested_units > 0 ? GARNET : ASHEN }}>
                    {v.suggested_units > 0 ? `${v.suggested_order_qty} ${v.order_unit === 'pair' ? 'pr' : 'u'}` : '—'}
                  </td>
                  <td style={{ padding: '9px 12px', textAlign: 'right' }}>
                    {v.float ? <span style={{ color: RAG[v.float.rag] || ASHEN, fontWeight: 600 }}>
                      {v.float.rag.toUpperCase()}{v.float.cash_neutral_pct != null ? ` ${v.float.cash_neutral_pct}%` : ''}
                    </span> : <span style={{ color: ASHEN }}>—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && (
        <div style={{ fontSize: 12, color: ASHEN, marginTop: 10 }}>
          <strong>Tier</strong>: A = protected top sellers (never-zero). <strong>Suggest</strong> = qty to order in the factory's unit.
          <strong> Float</strong> = % of the batch that must sell to cover the 70% balance before it's due (green comfortable, red = dead-stock risk).
        </div>
      )}
    </div>
  );
}
